import { useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Avatar, Box, Button, Typography } from "@mui/material";
import MyBreakcrumbs from "../components/MyBreadcrumbs";
import MyTable from "../components/MyTable";
import AddDoctorModal from "../components/AddDoctorModal";
import SearchInput from "../components/SearchInput"; // Import the SearchInput component.
import { jwtDecode } from "jwt-decode";
import AvailabilityDays from "../components/AvailabilityDays";
import { FiPlus } from "react-icons/fi";
import DoctorDetails from "../components/DoctorDetails";
import { IoCloseOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { serviceFetchDoctorList, serviceToggleDoctorStatus } from "../services/api";
import ConfirmBox from "../components/ConfirmBox";
import { FaToggleOn, FaToggleOff, FaEye, FaEdit } from "react-icons/fa";

function DoctorList() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shouldShowAddDoctorForm = queryParams.get("addDoctor") === "true";

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState("");
  const [decodedUserId, setDecodedUserId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);


  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            alt={row.original.first_name + " " + row.original.last_name}
            src={row.original.avatarUrl}
          />
          <Typography ml={1}>
            {row.original.first_name + " " + row.original.last_name}
          </Typography>
        </Box>
      ),
    },
    {
      Header: "Speciality",
      accessor: "doctor_speciality",
      Cell: ({ row }) => row.original.doctor_speciality || "",
    },
    {
      Header: "Exp",
      accessor: "exp",
      Cell: ({ row }) => row.original.experience || "",
    },
    {
      Header: "Qualification",
      accessor: "doctor_qualification",
      Cell: ({ row }) => row.original.doctor_qualification || "",
    },
    // {
    //   Header: "Todays appointments",
    //   // Since Todays appointments data is not provided, we don't need an accessor
    //   Cell: () => "",
    // },
    {
      Header: "Consultation fee",
      accessor: "consultation_fees",
      Cell: ({ row }) => row.original.consultation_fees || "",
    },
    {
      Header: "Availability",
      id: "availability",
      Cell: ({ row }) => {
        const { days, hours } = row.original.availibility;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            <AvailabilityDays days={days || []} />
          </Box>
        );
      },
    },
    {
      Header: "Status",
      accessor: "staff_status",
      Cell: ({ row }) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.original.staff_status === 1
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.original.staff_status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: ({ row }) => (
        <div className="flex gap-3 items-center">
          <FaEye
            className="text-blue-600 text-lg cursor-pointer"
            title="View"
            onClick={() => handleViewDoctor(row.original)}
          />
          <FaEdit
            className="text-blue-600 text-lg cursor-pointer"
            title="Edit"
            onClick={() => handleEditDoctor(row.original)}
          />
          <ConfirmBox
            title={row.original.staff_status === 1 ? "Deactivate Doctor" : "Activate Doctor"}
            message={`Are you sure you want to ${row.original.staff_status === 1 ? "deactivate" : "activate"} ${row.original.first_name} ${row.original.last_name}?`}
            onConfirm={() => handleToggleStatus(row.original.doctor_id)}
          >
            {row.original.staff_status === 1 ? (
              <FaToggleOn className="text-green-600 text-2xl cursor-pointer" title="Active - click to deactivate" />
            ) : (
              <FaToggleOff className="text-gray-400 text-2xl cursor-pointer" title="Inactive - click to activate" />
            )}
          </ConfirmBox>
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    {
      label: "Home",
      href: "#",

      class: "text-[#2E37A4]",
    },
    { label: "Doctor", class: "text-[#2E37A4]" },
  ];

  //const [error, setError] = useState(null);
  const [showAddDoctorForm, setShowAddDoctorForm] = useState(
    shouldShowAddDoctorForm
  );

  
  const [filterInput, setFilterInput] = useState("");

  const handleFilterChange = (event) => {
    const value = event.target.value || "";
    setFilterInput(value);
  };

  const handleOpenAddDoctorForm = () => {
    setShowAddDoctorForm(true);
    //setWorkingDays(defaultWorkingDays);
    //setWorkingHours(defaultWorkingHours);
  };

  const handleCloseProfile = () => {
    setSelectedDoctor(null);
  };

  const handleModalClose = () => {
    setShowAddDoctorForm(false);
    setSelectedDoctor(null);
    setIsEdit(false);
  ;}


  const handleViewDoctor = (doctorId) => {
    // Navigate to the doctor details page with the doctor's ID
    // history.push(`/doctors/${doctorId}`);
    setSelectedDoctor(doctorId);
  };

  const handleEditDoctor = (doctorId) => {
    setIsEdit(true);
    setShowAddDoctorForm(true);
    setSelectedDoctor(doctorId);
  };

  const handleToggleStatus = async (doctorId) => {
    const response = await serviceToggleDoctorStatus(doctorId);
    if (response?.status) {
      fetchUserIdAndDoctorsCorresponding();
    }
  };
  
  const fetchUserIdAndDoctorsCorresponding = async () => {

    const token = localStorage.getItem("token");
    const decodedToken = jwtDecode(token);
  
    const response = await serviceFetchDoctorList(decodedToken.user_id);
      if(response?.status){
      const data = response?.data;
      const transformedDoctors = (data?.doctors || []).map((doctor) => ({
        ...doctor,
        name: `${doctor.first_name} ${doctor.last_name}`,
        doctor_speciality: doctor.doctor_speciality || "",
        exp: doctor.experience || "Not Available",
        consultation_fees: doctor.consultation_fees || "Not Available",
        doctor_qualification: doctor.doctor_qualification || "",
        staff_status: doctor.staff_status,
        todaysAppointments: "", // Since "Todays appointments" is not provided
        visitDays: "", // we have to remove this, no longer needed.
      }));
    
      setDoctors(transformedDoctors);
    }else{
      setError(response.message);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const decodedToken = jwtDecode(token);
    setDecodedUserId(decodedToken.user_id); // Save decoded user_id
    fetchUserIdAndDoctorsCorresponding();
  }, []);

  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 py-4 sm:py-6 md:px-8">
      <div className="pt-2 pb-6">
        <MyBreakcrumbs data={breadcrumbs} />
      </div>

      <div className=" bg-white rounded-xl shadow-md shadow-gray-200 ">
        <div className=" px-5 py-3 flex flex-wrap md:flex-nowrap justify-between items-center gap-y-5">
          <div>
            <div className=" text-lg  font-semibold  text-gray-800">Doctor</div>
          </div>

          <div className=" inline-flex">
            <SearchInput
              filterInput={filterInput}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>

        <div className=" overflow-auto px-4">
          <MyTable
            columns={columns}
            data={doctors}
            filterInput={filterInput}
            handleFilterChange={handleFilterChange}
            type="doctorsList"
          />
        </div>
        {/* Render the table */}
      </div>

      <AnimatePresence>
        {selectedDoctor && !isEdit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className={`bg-white shadow-md rounded-none fixed right-0 top-0 h-full z-40 w-full lg:w-[calc(100%-322px)] xl:w-[calc(100%-65%)] 2xl:w-[calc(100%-65%)]`}
          >
            <button
              className=" text-gray-600 bg-transparent hover:bg-gray-100 hover:text-gray-800 px-0 w-10 h-10 inline-flex justify-center items-center rounded-full text-2xl m-1.5"
              onClick={handleCloseProfile}
            >
              <IoCloseOutline />
            </button>
            <DoctorDetails doctor={selectedDoctor} />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={handleOpenAddDoctorForm}
        type="button"
        className="  text-2xl p-0 fixed right-3 bottom-3 flex items-center justify-center text-white bg-blue-700 rounded-full w-12 h-12 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:focus:ring-blue-800"
      >
        <FiPlus />
        <span className="sr-only">Add Patient</span>
      </button>

      <AddDoctorModal
        isOpen={showAddDoctorForm}
        onClose={handleModalClose}
        fetchDoctorList={fetchUserIdAndDoctorsCorresponding}
        isEdit={isEdit}
        doctor={selectedDoctor}
      />
    </div>
  );
}

export default DoctorList;
